import { Injectable } from '@angular/core';
import { ThumbnailUtils } from './thumbnails.utils';
import { renderAsync } from 'docx-preview';
import html2canvas from 'html2canvas';
import * as pdfjsLib from 'pdfjs-dist';

@Injectable({
  providedIn: 'root',
})
export class ThumbnailService {
  constructor() {
    pdfjsLib.GlobalWorkerOptions.workerSrc = '/assets/pdfjs/pdf.worker.min.js';
  }

  previewSize = 256;

  generateThumbnail(
    file: File,
    thumbnailFileName: string,
    callback: (thumbnail: File) => void
  ): void {
    const fileType = file.type;

    switch (true) {
      case fileType.startsWith('image/'):
        this.generateImageThumbnail(file, thumbnailFileName, callback);
        break;
      case fileType.startsWith(
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ):
        this.generateWordThumbnail(file, thumbnailFileName, callback);
        break;
      case fileType.startsWith('application/pdf'):
        this.generatePDFThumbnail(file, thumbnailFileName, callback);
        break;
      case fileType.startsWith('video/'):
        this.generateVideoThumbnail(file, thumbnailFileName, callback);
        break;
      default:
        console.warn('Unsupported file type:', fileType);
    }
  }

  generateImageThumbnail(
    file: File,
    fileName: string,
    callback: (thumbnail: File) => void
  ) {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const image = new Image();
      image.src = reader.result as string;
      image.onload = function () {
        const resizedDataUrl = ThumbnailUtils.compressAndResizeImage(image);
        callback(ThumbnailUtils.b64toFile(resizedDataUrl, fileName));
      };

      image.onerror = function () {
        callback(null);
      };
    };
    reader.onerror = () => {
      callback(null);
    };
  }

  generateWordThumbnail(
    file: File,
    fileName: string,
    callback: (thumbnail: File) => void
  ) {
    const blob = new Blob([file], { type: file.type });

    const docDiv = document.createElement('div');
    docDiv.id = 'docxWrapperDiv';
    docDiv.style.width = '595px';
    docDiv.style.height = '842px';
    docDiv.style.padding = '10px';
    docDiv.style.display = 'none';

    renderAsync(blob, docDiv, null, { inWrapper: false }).then(() => {
      document.body.appendChild(docDiv);

      html2canvas(docDiv, {
        onclone: (cloned) => {
          cloned.getElementById('docxWrapperDiv').style.display = 'block';
        },
      }).then((canvas) => {
        document.body.appendChild(canvas);
        const dataUrl = canvas.toDataURL('image/png', 1.0);
        const imageFile = ThumbnailUtils.b64toFile(dataUrl, fileName);
        this.generateImageThumbnail(imageFile, fileName, callback);
        docDiv.remove();
        canvas.remove();
      });
    });
  }

  generatePDFThumbnail(
    file: File,
    fileName: string,
    callback: (thumbnail: File) => void
  ) {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = async () => {
      try {
        const pdfData = reader.result as ArrayBuffer;
        const pdfDoc = await pdfjsLib.getDocument({
          data: pdfData,
          isEvalSupported: false,
        }).promise;
        const page = await pdfDoc.getPage(1);

        const viewport = page.getViewport({ scale: 1 });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const context = canvas.getContext('2d');

        await page.render({
          canvasContext: context,
          viewport: viewport,
        }).promise;

        const dataUrl = canvas.toDataURL('image/png', 1.0);
        const imageFile = ThumbnailUtils.b64toFile(dataUrl, fileName);
        callback(imageFile);
      } catch (error) {
        console.error('PDF thumbnail generation failed:', error);
        callback(null);
      }
    };
    reader.onerror = () => {
      callback(null);
    };
  }

  generateVideoThumbnail(
    file: File,
    fileName: string,
    callback: (thumbnail: File) => void
  ) {
    const videoURL = URL.createObjectURL(file);

    const video = document.createElement('video');
    video.src = videoURL;
    video.autoplay = false;
    video.pause();
    video.currentTime = 1; // Forwards the video seeker to 1 second to avoid starting transitions or blank screens

    video.onloadeddata = () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas
        .getContext('2d')
        .drawImage(video, 0, 0, video.videoWidth, video.videoHeight);

      const imageURL = canvas.toDataURL('image/jpeg', 1);
      const imageFile = ThumbnailUtils.b64toFile(imageURL, fileName);

      this.generateImageThumbnail(imageFile, fileName, callback);
    };
  }
}
